import { z } from 'zod';
import { localizedTextSchema, systemRoleKeySchema } from '../access/index';
import { languageSchema } from '../auth/index';
import { loginDefaultSchema, orgTypeSchema } from './org-type';

/**
 * Het startprofiel van een type organisatie, bewerkbaar in Master (ORG-3, ADR-0099).
 *
 * Een eigen bestand naast `index.ts`, zoals `brand.ts` en `master.ts`: het is een afgebakend deel,
 * en het heeft de rolsleutels van `access` nodig.
 */

/** De naam van elke systeemrol, per taal. Voor een type: alle zes, opgelost. */
export const orgTypeRoleNamesSchema = z.record(systemRoleKeySchema, localizedTextSchema);
export type OrgTypeRoleNames = z.infer<typeof orgTypeRoleNamesSchema>;

/** Wat een profiel bepaalt: de vlaggen uit de beheerde set, de aanmeldwijze en de rolnamen. */
export const orgTypeProfileSettingsSchema = z.object({
  /** Elke vlag uit `profileFlags` van de lijst, met de stand die een nieuwe organisatie krijgt. */
  flags: z.record(z.string(), z.boolean()),
  loginDefault: loginDefaultSchema,
  roleNames: orgTypeRoleNamesSchema,
});
export type OrgTypeProfileSettings = z.infer<typeof orgTypeProfileSettingsSchema>;

/**
 * Het profiel van één type zoals Master het toont (ORG-3): wat er geldt, wat de code zegt, en welk
 * deel Master daarover zette.
 */
export const orgTypeProfileSchema = orgTypeProfileSettingsSchema.extend({
  orgType: orgTypeSchema,
  /** Het profiel uit de code, zonder Master: voor "terug naar standaard" en de plaatshouders. */
  codeDefault: orgTypeProfileSettingsSchema,
  /** Welke delen Master zette. Een deel dat `false` is, volgt de code, ook na een update. */
  overridden: z.object({
    flags: z.boolean(),
    loginDefault: z.boolean(),
    roleNames: z.boolean(),
  }),
  /** Hoeveel organisaties (niet gearchiveerd) dit type nu hebben. */
  tenantCount: z.number().int(),
  updatedAt: z.string().datetime().nullable(),
});
export type OrgTypeProfile = z.infer<typeof orgTypeProfileSchema>;

export const orgTypeProfileListSchema = z.object({
  /** De vlaggen die een profiel beheert, in de volgorde van het scherm. */
  profileFlags: z.array(z.string()),
  items: z.array(orgTypeProfileSchema),
});
export type OrgTypeProfileList = z.infer<typeof orgTypeProfileListSchema>;

/**
 * Het profiel van een type wijzigen in Master (ORG-3).
 *
 * Per deel: weggelaten = ongewijzigd, `null` = terug naar de code. `flags` en `roleNames` mogen
 * onvolledig zijn; wat ontbreekt, volgt de code. Een rolnaam is per taal: een lege taal valt terug op
 * die van de code.
 */
export const updateOrgTypeProfileRequestSchema = z.object({
  flags: z.record(z.string(), z.boolean()).nullable().optional(),
  loginDefault: loginDefaultSchema.nullable().optional(),
  // Een lege naam mag: het formulier stuurt elk veld mee, en de server laat een leeg veld weg.
  roleNames: z
    .partialRecord(systemRoleKeySchema, z.partialRecord(languageSchema, z.string().trim().max(60)))
    .nullable()
    .optional(),
});
export type UpdateOrgTypeProfileRequest = z.infer<typeof updateOrgTypeProfileRequestSchema>;
